import { useState, useEffect, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import { createCrmContact } from "../../../services/crmContacts";
import { ProspectTypeahead } from "../../../components/ProspectTypeahead";
import type { ProspectOption } from "../../../components/ProspectTypeahead";
import { CrmTeamTypeahead } from "../../../components/CrmTeamTypeahead";
import type { CrmTeamOption } from "../../../components/CrmTeamTypeahead";
import { HandleInput } from "../../../components/HandleInput";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import { SelectPopover } from "../../../components/SelectPopover";
import { COACH_TITLES } from "../../../constants/coachTitles";
import {
  availablePreferredContactMethodLabels,
  preferredContactMethodLabel,
  preferredContactMethodValue,
} from "../../../constants/preferredContactMethods";
import { formatPhoneNumber, stripPhoneFormat } from "../../../utils/formatters";
import { usePlayerFormState } from "../../players/playerForm/usePlayerFormState";
import { PlayerFormSections } from "../../players/playerForm/PlayerFormSections";
import "../../players/playerForm/PlayerForm.css";
import "./AddContactModal.css";

interface AddContactModalProps {
  isOpen: boolean;
  contactType: string;
  onClose: () => void;
  onSuccess: (createdId?: string) => void;
  initialXHandle?: string;
  initialFirstName?: string;
  initialLastName?: string;
}

const TYPE_LABELS: Record<string, string> = {
  Coach: "Coach",
  ProspectProfile: "Player",
  Parent: "Parent",
};

interface FormErrors {
  [key: string]: string | undefined;
}

export const AddContactModal = ({
  isOpen,
  contactType,
  onClose,
  onSuccess,
  initialXHandle,
  initialFirstName,
  initialLastName,
}: AddContactModalProps) => {
  // Coach/Parent shared fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [xHandle, setXHandle] = useState("");
  const [slackUsername, setSlackUsername] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState("");
  const [notes, setNotes] = useState("");

  // Coach-specific
  const [title, setTitle] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<CrmTeamOption | null>(null);

  // Parent-specific
  const [selectedProspect, setSelectedProspect] = useState<ProspectOption | null>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  const [globalError, setGlobalError] = useState("");
  const [saving, setSaving] = useState(false);

  // Shared player-form state (used only for ProspectProfile)
  const playerForm = usePlayerFormState();

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setXHandle("");
      setSlackUsername("");
      setPreferredContactMethod("");
      setNotes("");
      setTitle("");
      setSelectedTeam(null);
      setSelectedProspect(null);
      setErrors({});
      setGlobalError("");
      setSaving(false);
      playerForm.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Hydrate initial values
  useEffect(() => {
    if (!isOpen) return;
    if (contactType === "ProspectProfile") {
      if (initialXHandle) playerForm.setters.set("xHandle", initialXHandle.replace(/@/g, ""));
      if (initialFirstName) playerForm.setters.set("firstName", initialFirstName);
      if (initialLastName) playerForm.setters.set("lastName", initialLastName);
    } else {
      if (initialXHandle) setXHandle(initialXHandle.replace(/@/g, ""));
      if (initialFirstName) setFirstName(initialFirstName);
      if (initialLastName) setLastName(initialLastName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialXHandle, initialFirstName, initialLastName, contactType]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const dialogRef = useRef<HTMLFormElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  const typeLabel = TYPE_LABELS[contactType] ?? contactType;

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    if (contactType === "ProspectProfile") {
      return playerForm.validate();
    }

    const newErrors: FormErrors = {};
    if (!firstName.trim()) newErrors.firstName = "Required";
    if (!lastName.trim()) newErrors.lastName = "Required";

    if (contactType === "Coach") {
      if (!title.trim()) newErrors.title = "Required";
      if (!email.trim() && !phone.trim() && !xHandle.trim())
        newErrors.email = "Enter at least one of: email, phone, or X handle";
    }

    if (contactType === "Parent") {
      if (!email.trim() && !phone.trim()) newErrors.email = "Email or phone required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).filter((k) => newErrors[k]).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setGlobalError("");
    setSaving(true);
    try {
      let payload: Record<string, unknown>;

      if (contactType === "ProspectProfile") {
        const playerPayload = playerForm.buildPayload();
        // PlayerFormPayload is JSON-shape compatible with CreateContactRequest;
        // gradYear must be sent as a string per current contract.
        payload = {
          contactType,
          ...playerPayload,
          gradYear: String(playerPayload.gradYear),
        };
      } else if (contactType === "Coach") {
        payload = {
          contactType,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim() || undefined,
          phone: stripPhoneFormat(phone) || undefined,
          roleTitle: title.trim(),
          crmTeamId: selectedTeam?.id || undefined,
          xHandle: xHandle.trim() || undefined,
          preferredContactMethod: preferredContactMethod || undefined,
          notes: notes.trim() || undefined,
        };
      } else {
        // Parent
        payload = {
          contactType,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim() || undefined,
          phone: stripPhoneFormat(phone) || undefined,
          prospectId: selectedProspect?.id,
          xHandle: xHandle.trim() || undefined,
          slackUsername: slackUsername.trim() || undefined,
          notes: notes.trim() || undefined,
        };
      }

      const result = await createCrmContact(payload);
      onSuccess(result?.id);
    } catch {
      setGlobalError("Failed to save contact. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const isFormComplete = (() => {
    if (contactType === "ProspectProfile") {
      const s = playerForm.state;
      if (!s.firstName.trim() || !s.lastName.trim()) return false;
      if (!s.email.trim()) return false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim())) return false;
      if (!s.phone.trim() || stripPhoneFormat(s.phone).length !== 10) return false;
      if (!s.position || !s.bats || !s.throws) return false;
      if (!s.heightFeet.trim() || !s.weight.trim()) return false;
      if (!s.school.trim() || !s.city.trim() || !s.state) return false;
      if (!s.gradYear.trim()) return false;
      if (s.preferredLocations.length === 0) return false;
      return true;
    }

    if (!firstName.trim() || !lastName.trim()) return false;

    if (contactType === "Coach") {
      if (!title.trim()) return false;
      if (!email.trim() && !phone.trim() && !xHandle.trim()) return false;
    }

    if (contactType === "Parent") {
      if (!selectedProspect) return false;
      if (!email.trim() && !phone.trim()) return false;
    }

    return true;
  })();

  const isSaveDisabled = saving || !isFormComplete;

  const renderField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts?: { type?: string; placeholder?: string; required?: boolean; errorKey?: string }
  ) => {
    const errKey = opts?.errorKey || label.toLowerCase().replace(/[^a-z]/g, "");
    return (
      <div className="add-contact-modal__field">
        <label className="add-contact-modal__label">
          {label}
          {opts?.required ? " *" : ""}
        </label>
        <input
          className={`add-contact-modal__input${
            errors[errKey] ? " add-contact-modal__input--error" : ""
          }`}
          type={opts?.type || "text"}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            clearError(errKey);
          }}
          placeholder={opts?.placeholder}
        />
        {errors[errKey] && (
          <span className="add-contact-modal__field-error">{errors[errKey]}</span>
        )}
      </div>
    );
  };

  const isProspect = contactType === "ProspectProfile";

  return (
    <div
      className="add-contact-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form ref={dialogRef} tabIndex={-1}
        className={`add-contact-modal${isProspect ? " add-contact-modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Add ${typeLabel}`}
        onSubmit={handleSubmit}
      >
        <div className="add-contact-modal__header">
          <h2 className="add-contact-modal__title">Add {typeLabel}</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="add-contact-modal__body">
          {isProspect ? (
            <div className="submission-form">
              <PlayerFormSections
                state={playerForm.state}
                setters={playerForm.setters}
                errors={playerForm.errors}
                disabled={saving}
                showRecruitmentStage
                showProjectedDivisions
              />
            </div>
          ) : (
            <>
              <div className="add-contact-modal__row">
                {renderField("First Name", firstName, setFirstName, {
                  required: true,
                  errorKey: "firstName",
                })}
                {renderField("Last Name", lastName, setLastName, {
                  required: true,
                  errorKey: "lastName",
                })}
              </div>

              <div className="add-contact-modal__row">
                {renderField("Email", email, setEmail, {
                  type: "email",
                  required: contactType === "Parent",
                  errorKey: "email",
                })}
                <div className="add-contact-modal__field">
                  <label className="add-contact-modal__label">Phone{contactType === "Parent" ? " *" : ""}</label>
                  <input
                    className={`add-contact-modal__input${
                      errors.phone ? " add-contact-modal__input--error" : ""
                    }`}
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(formatPhoneNumber(e.target.value));
                      clearError("phone");
                    }}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && (
                    <span className="add-contact-modal__field-error">{errors.phone}</span>
                  )}
                </div>
              </div>

              <div className="add-contact-modal__field">
                <label htmlFor="add-contact-modal-notes-0" className="add-contact-modal__label">Notes</label>
                <textarea id="add-contact-modal-notes-0"
                  className="add-contact-modal__input add-contact-modal__textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Internal notes..."
                />
              </div>

              {contactType === "Coach" && (
                <>
                  <div className="add-contact-modal__row">
                    <HandleInput
                      label="X Handle (optional)"
                      value={xHandle}
                      onChange={(v) => {
                        setXHandle(v);
                        if (!v && preferredContactMethod === "X") setPreferredContactMethod("");
                      }}
                      placeholder="handle"
                      variant="dashboard"
                      wrapperClassName="add-contact-modal__field"
                    />
                    <div className="add-contact-modal__field">
                      <label htmlFor="add-contact-modal-preferred-contact-1" className="add-contact-modal__label">Preferred Contact</label>
                      <SelectPopover
                        id="add-contact-modal-preferred-contact-1"
                        options={availablePreferredContactMethodLabels(email, phone, xHandle)}
                        value={preferredContactMethodLabel(preferredContactMethod)}
                        onChange={(label) => setPreferredContactMethod(preferredContactMethodValue(label) ?? "")}
                        placeholder="Select..."
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className="add-contact-modal__field">
                    <label htmlFor="add-contact-modal-title-role-2" className="add-contact-modal__label">Title / Role *</label>
                    <SelectPopover
                      id="add-contact-modal-title-role-2"
                      options={COACH_TITLES}
                      value={title}
                      onChange={(v) => {
                        setTitle(v);
                        clearError("title");
                      }}
                      placeholder="Select..."
                      fullWidth
                    />
                    {errors.title && (
                      <span className="add-contact-modal__field-error">{errors.title}</span>
                    )}
                  </div>
                  <div className="add-contact-modal__field add-contact-modal__field--typeahead">
                    <label htmlFor="add-contact-modal-team-optional-0" className="add-contact-modal__label">Team (optional)</label>
                    <CrmTeamTypeahead inputId="add-contact-modal-team-optional-0"
                      value={selectedTeam}
                      onSelect={setSelectedTeam}
                      placeholder="Search teams..."
                    />
                  </div>
                </>
              )}

              {contactType === "Parent" && (
                <>
                  <div className="add-contact-modal__field add-contact-modal__field--typeahead">
                    <label htmlFor="add-contact-modal-related-prospect-1" className="add-contact-modal__label">Related Prospect *</label>
                    <ProspectTypeahead inputId="add-contact-modal-related-prospect-1"
                      value={selectedProspect}
                      onSelect={setSelectedProspect}
                      placeholder="Search by prospect name..."
                    />
                  </div>
                  <div className="add-contact-modal__row">
                    <HandleInput
                      label="X Username (optional)"
                      value={xHandle}
                      onChange={setXHandle}
                      placeholder="handle"
                      variant="dashboard"
                      wrapperClassName="add-contact-modal__field"
                    />
                    <div className="add-contact-modal__field">
                      <label htmlFor="add-contact-modal-slack-username-optional-3" className="add-contact-modal__label">Slack Username (optional)</label>
                      <input id="add-contact-modal-slack-username-optional-3"
                        className="add-contact-modal__input"
                        type="text"
                        value={slackUsername}
                        onChange={(e) => setSlackUsername(e.target.value)}
                        placeholder="username or email"
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {globalError && <div className="add-contact-modal__error">{globalError}</div>}
        </div>

        <div className="add-contact-modal__footer">
          <button type="button" className="add-contact-modal__cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="add-contact-modal__save" disabled={isSaveDisabled}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};
